import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { IndicatorType } from "../models/indicator-types";
import { Language } from "../models/language";
import {
  QuestionType,
  SaveAdministrativeUnitSurveyMessage,
  Survey,
  SurveyAnswerModel,
  TenantSurvey,
  AdministrativeUnitSurveyMessage,
} from "../models/survey";
import { WorkflowStatusChange } from "../models/workflow";
import { DataListTagGroup } from "../models/data-list";
import { SearcDocumentsPagedResponse } from "../models/search-documents-paged-response";

@Injectable({
  providedIn: "root",
})
export class PublicSurveyService {
  private baseUrl = "api/public/surveys";

  constructor(private http: HttpClient) {}

  get(surveyId: string, languageId: string, administrativeUnitId: string = null) {
    if (administrativeUnitId) {
      return this.http.get<Survey>(
        `${this.baseUrl}/${surveyId}/${languageId}/${administrativeUnitId}`
      );
    }
    return this.http.get<Survey>(`${this.baseUrl}/${languageId}/${surveyId}`);
  }

  getAdministrativeUnitSurvey(administrativeUnitSurveyId: string) {
    return this.http.get<TenantSurvey>(
      `${this.baseUrl}/tenantsurvey/${administrativeUnitSurveyId}`
    );
  }

  saveAnswer(answer: SurveyAnswerModel): Observable<SurveyAnswerModel> {
    const headerDict = {
      "no-spinner": "stops spinner from showing",
    };

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    };

    return this.http.post<SurveyAnswerModel>(
      `${this.baseUrl}/saveanswer`,
      answer,
      requestOptions
    );
  }

  getQuestionTypes() {
    return this.http.get<QuestionType[]>(`${this.baseUrl}/question-types`);
  }

  getTagGroups() {
    return this.http.get<DataListTagGroup[]>(`${this.baseUrl}/tag-groups`);
  }

  search(key: string): Observable<string> {
    return this.http.post<string>(`${this.baseUrl}/search`, { surveyKey: key });
  }

  searchDocuments(administrativeUnitSurveyId: string) {
    return this.http.get<SearcDocumentsPagedResponse>(`${this.baseUrl}/get-fa-docs/${administrativeUnitSurveyId}`);
  }

  getDocument(docId: string, auCode: string) {
    return this.http.get<string>(`${this.baseUrl}/get-fa-doc/${auCode}/${docId}`, {
      responseType: "text" as "json",
    });
  }

  getLanguages(surveyId: string) {
    return this.http.get<Language[]>(`${this.baseUrl}/${surveyId}/languages`);
  }

  getLanguagesForCurrentSurvey(group: string) {
    return this.http.get<Language[]>(
      `${this.baseUrl}/languages/current/${group}`
    );
  }

  getLeaAuMonitoringIndicatorsForAdministrativeUnit(
    indicatorType: IndicatorType,
    administrativeUnitId: string,
    schoolYear: string
  ): Observable<any> {
    switch (indicatorType) {
      case IndicatorType.Eight:
      case IndicatorType.Fourteen:
        //case IndicatorType.Thirteen: //todo: add this back in when backend completed
        return this.http.get<any>(
          `${this.baseUrl}/${indicatorType}/lea/au/${administrativeUnitId}/${schoolYear}`
        );
      default:
        return of();
    }
  }

  submitAdministrativeUnitSurvey(
    indicatorType: number,
    administrativeUnitSurveyId: string,
    submissionStatus: string,
    outcomeTypeId: string = null,
    questionsToClear: string[] = null
  ) {
    const request = {
      submissionStatus: submissionStatus,
      outcomeTypeId: outcomeTypeId,
      questionsToClear: questionsToClear,
    };
    return this.http.post<void>(
      `${this.baseUrl}/${indicatorType}/lea/tenant-survey/${administrativeUnitSurveyId}/submit`,
      request
    );
  }

  getAvailableStatusChanges(administrativeUnitSurveyId: string, language: string) {
    return this.http.get<WorkflowStatusChange[]>(
      `${this.baseUrl}/${administrativeUnitSurveyId}/workflow-status-changes/${language}`
    );
  }

  changeSurveyStatus(
    workFlowId: string,
    administrativeUnitSurveyId: string,
    toWorkflowStatusId: string,
    indicatorType: number,
    languageId: string
  ) {
    var request = {
      workFlowId: workFlowId,
      administrativeUnitSurveyId: administrativeUnitSurveyId,
      toWorkflowStatusId: toWorkflowStatusId,
      indicatorType: indicatorType,
      languageId: languageId,
    };
    return this.http.put<void>(
      `${this.baseUrl}/update-administrativeUnit-survey-status`,
      request
    );
  }

  saveQuestionMessage(
    message: SaveAdministrativeUnitSurveyMessage
  ): Observable<AdministrativeUnitSurveyMessage> {
    const headerDict = {
      "no-spinner": "stops spinner from showing",
    };

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    };

    return this.http.post<AdministrativeUnitSurveyMessage>(
      `${this.baseUrl}/savequestionmessage`,
      message,
      requestOptions
    );
  }

  getFacilitatedAssessmentOpenSurvey(surveyCode: string, auCode: string) {
    return this.http.get<TenantSurvey>(
      `${this.baseUrl}/facilitatedAssessmentOpenSurvey/${surveyCode}/${auCode}`
    );
  }

  getFaAssignmentByAdministrativeUnitId(surveyId) {
    return this.http.get<any>(
      `${this.baseUrl}/fa-assignment/${surveyId}`
    );
  }
}
